import axios, { AxiosError, AxiosHeaders } from 'axios';
import i18n from 'i18next';

import { ApiUrl } from '../enums';

import { fetchRetailerApiSettings } from '~/api/easy-estimator-api/lookup-retailer-api/lookupRetailerApiService';
import { LOCAL_STORAGE_KEY } from '~/core/constants';
import { NotifierType, showNotification } from '~/legacy/components';

enum PDFType {
  INTERNAL = 'internal',
  RETAIL = 'retail',
}

interface EstimatePdfErrorResponse {
  statusCode: number;
  errorCode: string;
  errorMessage: string;
  validationErrors: string[];
}

interface DownloadPdfParams {
  code: string;
  filename: string;
  isInternalCost?: boolean;
  userToken?: string;
  password?: string;
}

const buildPdfParams = ({
  code,
  isInternalCost,
  userToken,
  password,
}: DownloadPdfParams): URLSearchParams => {
  const params = new URLSearchParams({
    [LOCAL_STORAGE_KEY.CODE]: code,
    [LOCAL_STORAGE_KEY.PDF_TYPE]: isInternalCost
      ? PDFType.INTERNAL
      : PDFType.RETAIL,
  });

  if (isInternalCost) {
    if (userToken) {
      params.set(LOCAL_STORAGE_KEY.USER_TOKEN, userToken);
    }
    if (password) {
      params.set(LOCAL_STORAGE_KEY.PASSWORD, password);
    }
  }

  return params;
};

const triggerFileDownload = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const handlePdfDownloadError = async (error: AxiosError): Promise<void> => {
  const errorBlob = error.response?.data as Blob;
  const errorData = JSON.parse(
    await errorBlob.text()
  ) as EstimatePdfErrorResponse;

  showNotification(
    NotifierType.ERROR,
    errorData.errorCode ?? i18n.t('NOTIFICATIONS.PRINT_ESTIMATE.ERROR.TITLE'),
    errorData.errorMessage,
    '',
    10
  );

  throw errorData;
};

export const postDownloadPdf = async ({
  code,
  filename,
  isInternalCost,
  userToken,
  password,
}: DownloadPdfParams): Promise<void> => {
  const { apiHostname: baseURL, token: bearerToken } =
    await fetchRetailerApiSettings();

  const fullUrl = new URL(ApiUrl.ESTIMATE_PDF, baseURL).toString();

  const params = buildPdfParams({
    code,
    filename,
    isInternalCost,
    userToken,
    password,
  });

  const headers = new AxiosHeaders(
    bearerToken ? `Authorization: Bearer ${bearerToken}` : undefined
  );

  try {
    const response = await axios.get(fullUrl, {
      responseType: 'blob',
      headers,
      params,
    });
    const pdfBlob = new Blob([response.data], { type: 'application/pdf' });

    triggerFileDownload(pdfBlob, filename);
  } catch (error) {
    await handlePdfDownloadError(error as AxiosError);
  }
};
